import {
  useEffect,
  useRef,
  useState,
  useContext,
  useMemo,
  Fragment,
} from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { DraggingContext } from './draggingContext';
import { useObservable } from './behaviorSubject';
import Draggable from 'react-draggable';
import useSyncScroll from 'react-use-sync-scroll';
import useEndsOfScroll from 'hooks/useEndsOfScroll';
import { hideScrollbarCss, borderRadii, layers } from 'app/spacing';
import BaseColumn from 'components/Board/BaseColumn';
import { Gradient as BaseGradient } from 'components/Kizen/ScrollFadeContainer';
import { useSyncSizes } from 'components/Tables/Big/hooks';
import { getItemDropzone } from 'components/DragAndDropLayout/helpers';
import { CardDropzone, StageDropzone } from './ObservableDropzone';
import ScrollBarDetached from 'components/Layout/ScrollBarDetached';
import ColumnTitle, { SecondarySubTitle, SubTitle } from '../ColumnTitle';
import Card from '../Card';
import { isStageField } from 'checks/fields';
import { getFieldAccessPermissions } from 'components/Fields/FieldInput/helpers';
import { SUMMARY_VALUES } from 'store/customObjectsRecordsPage/records.redux';
import { flushSync } from 'react-dom';
import { ScrollTriggers } from 'components/ScrollTrigger';

const CARDS_LOADED = 20;

const ContentWrapper = styled.div`
  display: flex;
  height: calc(
    100% - ${({ secondarySubTitle }) => (secondarySubTitle ? 83 : 63)}px
  ); // 63 - column title height, 83 - for secondary title line
`;

const Content = styled.div`
  padding: 2.5px 1px 2.5px 10px;
  width: 100%;
  overflow-y: auto;
  ${hideScrollbarCss}
`;

const Cards = styled.div`
  display: flex;
  flex-direction: column;
`;

const Wrapper = styled.div`
  position: relative;
  display: flex;

  .card {
    height: 100%;
    width: 100%;
  }

  ${({ draggingDimensions }) =>
    draggingDimensions &&
    css`
      z-index: ${layers.modal * 2};
      pointer-events: none; // TODO this makes the cursor change back to normal but allows mouseover
      .card {
        position: fixed;
        ${css(draggingDimensions)}
      }
    `}
`;

const CardPlaceholder = styled.div`
  ${({ dragging, cardHeight }) =>
    dragging
      ? css`
          position: relative;
          width: 100%;
          height: ${cardHeight}px;
          margin-bottom: 10px;
        `
      : css`
          position: absolute;
        `}
  top: 5px;
  bottom: 5px;
  left: 0;
  right: 0;
  background-color: var(--color-background-light);
  border: 1px dashed var(--color-border-medium);
  border-radius: ${borderRadii.small};
`;

const DRAG_SHIFT = 20;

const Gradient = styled((props) => (
  <BaseGradient fadeStart={0} fadeLength={50} {...props} />
))`
  // Always pulling gradient wrapper down for this case
  top: 100%;
  // Added space for half-height(2.5px) of the drop indicator
  // on the last item in the column. We need to move the gradient on this size too.
  bottom: -2.5px;
`;

const StyledOffsetParent = styled.div`
  position: ${({ isDragItem }) => (isDragItem ? 'fixed' : 'relative')};
`;

const OffsetParen = ({ children, isDragItem }) => {
  const ref = useRef();
  return (
    <>
      <StyledOffsetParent ref={ref} isDragItem={isDragItem} />
      {children({ offsetParentRef: ref })}
    </>
  );
};

const DesktopColumn = ({
  model,
  data,
  allowEdit,
  cards,
  columns,
  settings,
  teams,
  fields,
  stages,
  cardHeight,
  getMoreCards,
  setColumns,
  setDraggingData,
  setCardDropzone,
  setStageDropzone,
  onChangeSelection,
  onChoseMenu,
  onClickPlus,
  onSubmitRecord,
  onMoveCard,
  onChangeStage,
  applyCardDropzone,
  search,
  handleUpdateFieldOption,
  handleUpdateBoardViewRecords,
  ...props
}) => {
  const [stagedCards, setStagedCards] = useState(cards);

  const scrollbarContentWrapperRef = useRef();
  const dragPositionYRef = useRef();
  const dragShiftRef = useRef();
  const draggingCtxt = useContext(DraggingContext);
  const draggingData = useObservable(draggingCtxt.draggingData);
  const searchRef = useRef(search);
  const stageField = useMemo(() => fields.find(isStageField), [fields]);

  const onMouseMove = (ev) => {
    if (draggingData) {
      let shift;
      if (ev.clientY === dragPositionYRef.current) {
        shift = dragShiftRef.current;
      } else if (ev.clientY > dragPositionYRef.current) {
        shift = DRAG_SHIFT + cardHeight / 2;
      } else {
        shift = DRAG_SHIFT;
      }
      setStageDropzone({ id: data.id });
      setCardDropzone(
        getItemDropzone(
          draggingData.item,
          cards,
          '.CardItemWrapper',
          () => false,
          ev,
          shift
        )
      );
      dragShiftRef.current = shift;
      dragPositionYRef.current = ev.clientY;
    }
  };

  const [contentWrapperRefFn, contentWrapperRef] = useSyncSizes(
    scrollbarContentWrapperRef,
    '.ContentForSyncHeight',
    'height',
    [stagedCards]
  );
  useSyncScroll(useRef([contentWrapperRef, scrollbarContentWrapperRef]), {
    vertical: true,
  });

  const [, , bottomScrolled] = useEndsOfScroll(contentWrapperRef, [
    stagedCards,
  ]);

  useEffect(() => {
    setStagedCards(cards);
  }, [cards]);

  const isDragging = !!draggingData;

  useEffect(() => {
    if (data.hasMorePages && bottomScrolled && !isDragging) {
      // if the search is the same safe to request more pages
      if (search === searchRef.current) {
        // next page is based on the current cards in the column
        // we do this so that if cards are moved out the correct page is loaded
        const nextPage = Math.floor(data.cards.length / CARDS_LOADED) + 1;

        getMoreCards({
          stage: { id: data.id, name: data.name },
          page: nextPage,
        });
      } else if (data.page === 1) {
        // only reset when the data is back at the first page i.e. it's been refreshed
        searchRef.current = search;
        getMoreCards({
          stage: { id: data.id, name: data.name },
          page: data.page,
        });
      }
    }
  }, [bottomScrolled, getMoreCards, data, search, isDragging]);

  const getCardClassName = (itemId) =>
    `${!draggingData ? 'InlineHoverable' : ''}${
      draggingData?.item.id === itemId ? ' card' : ''
    }`;

  const wrapperRef = useRef(null);

  const handleOnStart = (ev, item) => {
    if (document.activeElement) {
      wrapperRef.current.click();
    }

    flushSync(() => {
      setDraggingData({
        dimensions: {
          height: ev.currentTarget.offsetHeight,
          width: ev.currentTarget.offsetWidth,
        },
        item,
        cardHeight: ev.currentTarget.offsetHeight,
      });
    });
  };

  const handleOnStop = () => {
    flushSync(() => {
      applyCardDropzone(cards, data, setStagedCards);
      setDraggingData(null);
      setCardDropzone(null);
      setStageDropzone(null);
    });
  };

  return (
    <BaseColumn
      onClickPlus={allowEdit && onClickPlus}
      columnWidth={settings.columnWidth}
      onMouseLeave={() => setStageDropzone(null)}
      data-qa={`board-column-${data.name}`}
      {...props}
    >
      <ColumnTitle
        title={data.name}
        subTitle={<SubTitle settings={settings} data={data} fields={fields} />}
        secondarySubTitle={
          <SecondarySubTitle settings={settings} data={data} fields={fields} />
        }
        isSelected={data.isChecked}
        onChangeSelection={onChangeSelection}
        checkBoxProps={{ zIndex: layers.content(0, 1) }}
      />
      <ContentWrapper
        secondarySubTitle={
          settings.secondarySummaryMetric !== SUMMARY_VALUES.noSummary
        }
        ref={wrapperRef}
      >
        <Content ref={contentWrapperRefFn} onMouseMove={onMouseMove}>
          <Cards key={data?.id} className="ContentForSyncHeight">
            {!data.cards.length ? (
              <StageDropzone margin={0} id={data.id} />
            ) : null}
            {stagedCards.map((item, index) => {
              const [isStageEditable] = getFieldAccessPermissions(
                stageField,
                item
              );

              const isDragItem = draggingData?.item.id === item.id;

              const onStart = (ev) => handleOnStart(ev, item);

              return (
                <Fragment key={item.id}>
                  <CardDropzone margin={0} id={item.id} position="before" />
                  <Wrapper
                    key={item.id}
                    className="CardItemWrapper"
                    draggingDimensions={
                      isDragItem ? draggingData.dimensions : null
                    }
                  >
                    <CardPlaceholder
                      dragging={isDragItem}
                      cardHeight={draggingData?.cardHeight}
                    />
                    <OffsetParen key={item.id} isDragItem={isDragItem}>
                      {({ offsetParentRef }) => (
                        <Draggable
                          handle=".CardHandle"
                          position={isDragItem ? null : { x: 0, y: 0 }}
                          offsetParent={offsetParentRef.current}
                          onStart={onStart}
                          onStop={handleOnStop}
                          disabled={!isStageEditable}
                        >
                          <Card
                            key={`${item.id}-${index}`}
                            className={getCardClassName(item.id)}
                            dragging={Boolean(draggingData)}
                            isDragItem={isDragItem}
                            data={item}
                            model={model}
                            stageStatus={data.status}
                            settings={settings}
                            teams={teams}
                            fields={fields}
                            stages={stages.filter((stage) => {
                              return stage.value !== item.stage.value;
                            })}
                            onChoseMenu={onChoseMenu}
                            onSubmitRecord={(recordId, patch) =>
                              onSubmitRecord(recordId, patch, {
                                stageId: data.id,
                              })
                            }
                            onChangeStage={(card, patch) =>
                              onChangeStage(card, patch, { stageId: data.id })
                            }
                            data-qa={item.id}
                            data-qa-index={index}
                            hideDnDIcon={!isStageEditable}
                            handleUpdateFieldOption={handleUpdateFieldOption}
                            selectOverlayObserverProps={{
                              root: null,
                              rootMargin: '0px 0px -38px 0px',
                              threshold: 1,
                            }}
                            rootCloseEvent="mousedown"
                            handleUpdateBoardViewRecords={
                              handleUpdateBoardViewRecords
                            }
                          />
                        </Draggable>
                      )}
                    </OffsetParen>
                  </Wrapper>
                  <CardDropzone margin={0} id={item.id} position="after" />
                </Fragment>
              );
            })}
          </Cards>
          <Gradient position="bottom" fadeIn={!bottomScrolled} />
        </Content>
        <ScrollBarDetached
          ref={scrollbarContentWrapperRef}
          direction="vertical"
          scrollClassName="ContentForSyncHeight"
        />
      </ContentWrapper>
      <ScrollTriggers
        scrollContainerRef={contentWrapperRef}
        scrollTriggers={[
          { position: 'top' },
          { position: 'bottom', offset: -30 },
        ]}
        enabled={isDragging}
        zIndex={layers.modal * 2 + 1}
      />
    </BaseColumn>
  );
};

export default DesktopColumn;
